// 🏆 BRACKET TREE - Tabellone Eliminazione Stile Tennistico
// Supporta: partita secca, andata/ritorno, tornei nativi e importati

import React, { useState, useEffect, useRef } from 'react';

// ============================================
// COSTANTI E HELPERS
// ============================================

const ROUND_NAMES = {
  'round_of_64': 'Trentaduesimi',
  'round_of_32': 'Sedicesimi',
  'round_of_16': 'Ottavi',
  'quarterfinal': 'Quarti',
  'semifinal': 'Semifinale',
  'final': 'Finale',
  'third_place': '3°/4° Posto',
  // Nomi italiani
  'sedicesimi': 'Sedicesimi',
  'ottavi': 'Ottavi',
  'quarti': 'Quarti',
  'semifinale': 'Semifinale',
  'finale': 'Finale'
};

const getRoundDisplayName = (round) => {
  if (!round) return 'Round';
  const lower = round.toLowerCase().replace(/_/g, ' ');
  return ROUND_NAMES[round] || ROUND_NAMES[lower] || round;
};

// Determina ordine round (per sorting)
const getRoundOrder = (round) => {
  const order = {
    'round_of_64': 1, 'round_of_32': 2, 'sedicesimi': 2,
    'round_of_16': 3, 'ottavi': 3,
    'quarterfinal': 4, 'quarti': 4,
    'semifinal': 5, 'semifinale': 5,
    'final': 6, 'finale': 6,
    'third_place': 7
  };
  return order[round?.toLowerCase()] || 0;
};

// ============================================
// MATCH CARD COMPONENT
// ============================================

const MatchCard = ({ 
  match, 
  isHomeAway, 
  onClick, 
  isAnimating,
  bracketName 
}) => {
  const [showAnimation, setShowAnimation] = useState(false);
  
  // Normalizza i dati (supporta sia formato nativo che importato)
  const teamA = match.team_a_name || match.giocatore_casa || match.player_a || '?';
  const teamB = match.team_b_name || match.giocatore_trasferta || match.player_b || '?';
  const scoreA = match.score_a ?? match.gol_casa ?? null;
  const scoreB = match.score_b ?? match.gol_trasferta ?? null;
  const scoreALeg2 = match.score_a_leg2 ?? match.gol_casa_ritorno ?? null;
  const scoreBLeg2 = match.score_b_leg2 ?? match.gol_trasferta_ritorno ?? null;
  const winnerId = match.winner_id || match.winner;
  const played = match.played || match.giocata || (scoreA !== null && scoreB !== null);
  const hasPenalties = match.has_penalties || (match.rigori_casa !== null && match.rigori_casa !== undefined);
  const penaltiesA = match.penalties_a ?? match.rigori_casa ?? null;
  const penaltiesB = match.penalties_b ?? match.rigori_trasferta ?? null;
  
  // Calcola stato partita A/R
  const hasLeg1 = scoreA !== null && scoreB !== null;
  const hasLeg2 = scoreALeg2 !== null && scoreBLeg2 !== null;
  const totalA = (scoreA || 0) + (scoreALeg2 || 0);
  const totalB = (scoreB || 0) + (scoreBLeg2 || 0);
  
  // Determina vincitore
  let winner = null;
  let isTeamAWinner = false;
  let isTeamBWinner = false;
  
  if (played || (isHomeAway && hasLeg1 && hasLeg2)) {
    if (winnerId) {
      isTeamAWinner = winnerId === match.team_a_id || winnerId === teamA;
      isTeamBWinner = winnerId === match.team_b_id || winnerId === teamB;
      winner = isTeamAWinner ? teamA : (isTeamBWinner ? teamB : null);
    } else if (isHomeAway && hasLeg2) {
      // Calcola vincitore A/R
      if (totalA > totalB) {
        isTeamAWinner = true;
        winner = teamA;
      } else if (totalB > totalA) {
        isTeamBWinner = true;
        winner = teamB;
      } else {
        // Parità - regola gol trasferta
        const awayGoalsA = scoreBLeg2 || 0; // Gol di A in trasferta (ritorno)
        const awayGoalsB = scoreB || 0; // Gol di B in trasferta (andata)
        if (awayGoalsA > awayGoalsB) {
          isTeamAWinner = true;
          winner = teamA;
        } else if (awayGoalsB > awayGoalsA) {
          isTeamBWinner = true;
          winner = teamB;
        }
        // Altrimenti rigori
      }
    } else if (!isHomeAway && hasLeg1) {
      if (scoreA > scoreB) {
        isTeamAWinner = true;
        winner = teamA;
      } else if (scoreB > scoreA) {
        isTeamBWinner = true;
        winner = teamB;
      } else if (hasPenalties) {
        if (penaltiesA > penaltiesB) {
          isTeamAWinner = true;
          winner = teamA;
        } else {
          isTeamBWinner = true;
          winner = teamB;
        }
      }
    }
  }
  
  // Stato partita
  const isFuture = teamA === '?' || teamB === '?' || (!teamA && !teamB);
  const isToPlay = !isFuture && !played && !(isHomeAway && hasLeg2);
  const isPartiallyPlayed = isHomeAway && hasLeg1 && !hasLeg2;
  const isComplete = played || (isHomeAway && hasLeg1 && hasLeg2);
  
  // Animazione
  useEffect(() => {
    if (isAnimating) {
      setShowAnimation(true);
      const timer = setTimeout(() => setShowAnimation(false), 500);
      return () => clearTimeout(timer);
    }
  }, [isAnimating]);
  
  // Stili
  const cardClasses = `
    relative rounded-lg border-2 cursor-pointer transition-all duration-300
    ${isComplete ? 'border-green-300 bg-green-50' : 
      isPartiallyPlayed ? 'border-blue-300 bg-blue-50' :
      isFuture ? 'border-gray-200 bg-gray-50' : 
      'border-gray-300 bg-white hover:border-blue-400 hover:shadow-md'}
    ${showAnimation ? 'scale-105 shadow-lg' : ''}
  `;
  
  // Render partita secca
  if (!isHomeAway) {
    return (
      <div 
        className={cardClasses}
        onClick={() => onClick && onClick(match)}
        data-testid={`match-card-${match.id}`}
      >
        <div className="p-3 min-w-[180px]">
          {/* Team A */}
          <div className={`flex items-center justify-between py-1.5 border-b border-gray-200 ${isTeamAWinner ? 'bg-green-100 -mx-3 px-3' : ''}`}>
            <span className={`font-medium truncate max-w-[120px] ${isTeamAWinner ? 'text-green-700 font-bold' : isFuture ? 'text-gray-400 italic' : 'text-gray-700'}`}>
              {isFuture && !teamA ? '? Vincitore' : teamA}
            </span>
            {hasLeg1 && (
              <span className={`font-bold ml-2 ${isTeamAWinner ? 'text-green-700' : 'text-gray-600'}`}>
                {scoreA}
              </span>
            )}
            {isTeamAWinner && <span className="text-green-500 ml-1">✓</span>}
          </div>
          
          {/* Team B */}
          <div className={`flex items-center justify-between py-1.5 ${isTeamBWinner ? 'bg-green-100 -mx-3 px-3' : ''}`}>
            <span className={`font-medium truncate max-w-[120px] ${isTeamBWinner ? 'text-green-700 font-bold' : isFuture ? 'text-gray-400 italic' : 'text-gray-700'}`}>
              {isFuture && !teamB ? '? Vincitore' : teamB}
            </span>
            {hasLeg1 && (
              <span className={`font-bold ml-2 ${isTeamBWinner ? 'text-green-700' : 'text-gray-600'}`}>
                {scoreB}
              </span>
            )}
            {isTeamBWinner && <span className="text-green-500 ml-1">✓</span>}
          </div>
          
          {/* Badge stato */}
          {isFuture && (
            <div className="mt-2 text-center">
              <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full">
                In attesa
              </span>
            </div>
          )}
          {isToPlay && !isFuture && (
            <div className="mt-2 text-center">
              <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full">
                Da giocare
              </span>
            </div>
          )}
          {hasPenalties && (
            <div className="mt-2 text-center">
              <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full">
                Rigori: {penaltiesA}-{penaltiesB}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // Render Andata/Ritorno
  return (
    <div 
      className={cardClasses}
      onClick={() => onClick && onClick(match)}
      data-testid={`match-card-${match.id}`}
    >
      <div className="p-3 min-w-[220px]">
        {/* Header */}
        <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-300">
          <span className={`font-semibold truncate ${isTeamAWinner ? 'text-green-700' : isTeamBWinner ? 'text-gray-500' : 'text-gray-800'}`}>
            {teamA}
          </span>
          <span className="text-gray-400 mx-2">vs</span>
          <span className={`font-semibold truncate ${isTeamBWinner ? 'text-green-700' : isTeamAWinner ? 'text-gray-500' : 'text-gray-800'}`}>
            {teamB}
          </span>
        </div>
        
        {/* Andata */}
        <div className="flex items-center justify-between py-1 text-sm">
          <span className="text-gray-500 flex items-center gap-1">
            <span>🏠</span> Andata:
          </span>
          {hasLeg1 ? (
            <span className="font-medium">
              {scoreA} - {scoreB} <span className="text-green-500 text-xs">✓</span>
            </span>
          ) : (
            <span className="text-gray-400 italic">Da giocare</span>
          )}
        </div>
        
        {/* Ritorno */}
        <div className="flex items-center justify-between py-1 text-sm">
          <span className="text-gray-500 flex items-center gap-1">
            <span>✈️</span> Ritorno:
          </span>
          {hasLeg2 ? (
            <span className="font-medium">
              {scoreALeg2} - {scoreBLeg2} <span className="text-green-500 text-xs">✓</span>
            </span>
          ) : (
            <span className="text-gray-400 italic">Da giocare</span>
          )}
        </div>
        
        {/* Aggregato / Parziale */}
        <div className="mt-2 pt-2 border-t border-gray-200">
          {hasLeg2 ? (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Aggregato:</span>
              <span className="font-bold text-gray-800">{totalA} - {totalB}</span>
            </div>
          ) : hasLeg1 ? (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Parziale:</span>
              <span className="font-medium text-gray-600">{scoreA} - {scoreB}</span>
            </div>
          ) : null}
          
          {/* Chi passa */}
          {(hasLeg2 || (isComplete && winner)) && winner && (
            <div className="mt-1 text-center">
              <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">
                ✅ Passa: {winner}
              </span>
            </div>
          )}
          
          {/* Rigori */}
          {hasPenalties && (
            <div className="mt-1 text-center">
              <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full">
                🔄 Rigori: {penaltiesA}-{penaltiesB}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// WINNER CARD COMPONENT
// ============================================

const WinnerCard = ({ winner, tournamentName }) => {
  if (!winner) return null;
  
  return (
    <div className="flex flex-col items-center justify-center">
      <div className="bg-gradient-to-br from-yellow-300 via-yellow-400 to-amber-500 rounded-xl p-4 shadow-lg border-2 border-yellow-500 min-w-[160px] text-center">
        <div className="text-3xl mb-2">🏆</div>
        <div className="text-xs text-yellow-800 font-medium uppercase tracking-wide mb-1">
          Campione
        </div>
        <div className="text-lg font-bold text-yellow-900">
          {winner}
        </div>
        {tournamentName && (
          <div className="text-xs text-yellow-700 mt-1 truncate max-w-[140px]">
            {tournamentName}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// ROUND COLUMN COMPONENT
// ============================================

const RoundColumn = ({ 
  roundName,
  displayName,
  matches, 
  isHomeAway, 
  onMatchClick,
  animatingMatches,
  bracketName,
  isLastRound 
}) => {
  // Calcola spacing tra partite basato sul numero
  const spacing = matches.length <= 2 ? 'gap-8' : matches.length <= 4 ? 'gap-6' : 'gap-4';
  
  return (
    <div className="flex flex-col min-w-[200px]">
      {/* Round Header */}
      <div className="text-center mb-4">
        <h4 className="text-sm font-bold text-gray-600 uppercase tracking-wide bg-gray-100 rounded-lg py-2 px-4">
          {displayName || getRoundDisplayName(roundName)}
        </h4>
        {isHomeAway && (
          <span className="text-xs text-blue-600 mt-1 inline-block">
            🔄 A/R
          </span>
        )}
      </div>
      
      {/* Matches */}
      <div className={`flex flex-col ${spacing} flex-1 justify-center`}>
        {matches.map((match, idx) => (
          <MatchCard
            key={match.id || idx}
            match={match}
            isHomeAway={isHomeAway || match.is_home_away}
            onClick={onMatchClick}
            isAnimating={animatingMatches?.includes(match.id)}
            bracketName={bracketName}
          />
        ))}
      </div>
    </div>
  );
};

// ============================================
// BRACKET CONNECTORS (SVG Lines)
// ============================================

const BracketConnectors = ({ rounds }) => {
  // SVG lines tra i round - implementazione base
  // Per semplicità usiamo spacing CSS invece di SVG complessi
  return null;
};

// ============================================
// MATCH DETAIL POPUP
// ============================================

const MatchDetailPopup = ({ match, isHomeAway, onClose, bracketName, roundName }) => {
  if (!match) return null;
  
  // Normalizza dati
  const teamA = match.team_a_name || match.giocatore_casa || '?';
  const teamB = match.team_b_name || match.giocatore_trasferta || '?';
  const scoreA = match.score_a ?? match.gol_casa ?? null;
  const scoreB = match.score_b ?? match.gol_trasferta ?? null;
  const scoreALeg2 = match.score_a_leg2 ?? match.gol_casa_ritorno ?? null;
  const scoreBLeg2 = match.score_b_leg2 ?? match.gol_trasferta_ritorno ?? null;
  const played = match.played || match.giocata || (scoreA !== null);
  const hasPenalties = match.has_penalties || match.rigori_casa;
  const penaltiesA = match.penalties_a ?? match.rigori_casa ?? null;
  const penaltiesB = match.penalties_b ?? match.rigori_trasferta ?? null;
  
  const hasLeg1 = scoreA !== null && scoreB !== null;
  const hasLeg2 = scoreALeg2 !== null && scoreBLeg2 !== null;
  const totalA = (scoreA || 0) + (scoreALeg2 || 0);
  const totalB = (scoreB || 0) + (scoreBLeg2 || 0);
  
  // Determina vincitore
  let winner = null;
  if (isHomeAway && hasLeg2) {
    if (totalA > totalB) winner = teamA;
    else if (totalB > totalA) winner = teamB;
    else {
      // Gol trasferta
      const awayA = scoreBLeg2 || 0;
      const awayB = scoreB || 0;
      if (awayA > awayB) winner = teamA;
      else if (awayB > awayA) winner = teamB;
      else if (hasPenalties) {
        winner = penaltiesA > penaltiesB ? teamA : teamB;
      }
    }
  } else if (!isHomeAway && played) {
    if (scoreA > scoreB) winner = teamA;
    else if (scoreB > scoreA) winner = teamB;
    else if (hasPenalties) {
      winner = penaltiesA > penaltiesB ? teamA : teamB;
    }
  }
  
  const isFuture = !played && !hasLeg1;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-lg">{getRoundDisplayName(roundName)}</h3>
              {bracketName && (
                <p className="text-blue-100 text-sm">{bracketName}</p>
              )}
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              ✕
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6">
          {isFuture ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">⏳</div>
              <p className="text-gray-600 font-medium">Partita non ancora disputata</p>
              <p className="text-gray-400 text-sm mt-2">
                In attesa della qualificazione dei partecipanti
              </p>
            </div>
          ) : isHomeAway ? (
            <>
              {/* Teams */}
              <div className="flex items-center justify-center gap-4 mb-6">
                <span className={`text-xl font-bold ${winner === teamA ? 'text-green-600' : 'text-gray-700'}`}>
                  {teamA}
                </span>
                <span className="text-gray-400">vs</span>
                <span className={`text-xl font-bold ${winner === teamB ? 'text-green-600' : 'text-gray-700'}`}>
                  {teamB}
                </span>
              </div>
              
              {/* Legs */}
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                  <span className="text-gray-600 flex items-center gap-2">
                    🏠 Andata
                  </span>
                  {hasLeg1 ? (
                    <span className="font-bold text-lg">{scoreA} - {scoreB}</span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </div>
                <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                  <span className="text-gray-600 flex items-center gap-2">
                    ✈️ Ritorno
                  </span>
                  {hasLeg2 ? (
                    <span className="font-bold text-lg">{scoreALeg2} - {scoreBLeg2}</span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </div>
              </div>
              
              {/* Aggregato */}
              {(hasLeg1 || hasLeg2) && (
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <span className="text-gray-600 text-sm">Aggregato</span>
                  <div className="text-2xl font-bold text-blue-700 mt-1">
                    {totalA} - {totalB}
                  </div>
                </div>
              )}
              
              {/* Winner */}
              {winner && (
                <div className="mt-4 text-center">
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full font-medium">
                    ✅ Passa il turno: <strong>{winner}</strong>
                  </span>
                </div>
              )}
              
              {/* Rigori */}
              {hasPenalties && (
                <div className="mt-3 text-center">
                  <span className="inline-flex items-center gap-2 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">
                    🔄 Rigori: {penaltiesA} - {penaltiesB}
                  </span>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Partita secca */}
              <div className="text-center mb-6">
                <div className="flex items-center justify-center gap-6">
                  <div className={`text-center ${winner === teamA ? 'text-green-600' : ''}`}>
                    <span className="text-xl font-bold block">{teamA}</span>
                    {played && <span className="text-3xl font-bold">{scoreA}</span>}
                  </div>
                  <span className="text-2xl text-gray-300">-</span>
                  <div className={`text-center ${winner === teamB ? 'text-green-600' : ''}`}>
                    <span className="text-xl font-bold block">{teamB}</span>
                    {played && <span className="text-3xl font-bold">{scoreB}</span>}
                  </div>
                </div>
              </div>
              
              {/* Winner */}
              {winner && (
                <div className="text-center">
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full font-medium">
                    ✅ Vincitore: <strong>{winner}</strong>
                  </span>
                </div>
              )}
              
              {/* Rigori */}
              {hasPenalties && (
                <div className="mt-3 text-center">
                  <span className="inline-flex items-center gap-2 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">
                    🔄 Rigori: {penaltiesA} - {penaltiesB}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Footer */}
        <div className="border-t border-gray-200 p-4">
          <button
            onClick={onClose}
            className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN BRACKET TREE COMPONENT
// ============================================

export const BracketTree = ({
  matches = [],
  rounds = [],
  knockoutConfig = {},
  winner = null,
  finalist = null,
  tournamentName = '',
  bracketName = '',
  thirdPlaceMatch = null,
  onMatchClick,
  isImported = false
}) => {
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [selectedRound, setSelectedRound] = useState(null);
  const [animatingMatches, setAnimatingMatches] = useState([]);
  const containerRef = useRef(null);
  
  // Organizza i match per round
  const organizeMatchesByRound = () => {
    const roundsMap = {};
    
    // Se abbiamo rounds strutturati (tornei importati con knockout_bracket.rounds)
    if (rounds && rounds.length > 0) {
      // Per tornei importati, i match sono raggruppati per fascia ma NON per turno
      // Dobbiamo separarli per "giornata" per creare colonne orizzontali
      rounds.forEach((round, idx) => {
        const matches = round.matches || [];
        
        if (matches.length === 0) return;
        
        // Raggruppa i match per giornata all'interno della fascia
        const matchesByGiornata = {};
        matches.forEach(match => {
          const giornata = match.giornata || match.round_num || 0;
          if (!matchesByGiornata[giornata]) {
            matchesByGiornata[giornata] = [];
          }
          matchesByGiornata[giornata].push(match);
        });
        
        // Converti in array ordinato per giornata
        const giornate = Object.keys(matchesByGiornata)
          .map(Number)
          .sort((a, b) => a - b);
        
        // Determina nome round in base al numero di match
        giornate.forEach((giornata, gIdx) => {
          const giornataMatches = matchesByGiornata[giornata];
          let roundName;
          
          // Determina nome round dal numero di partite
          if (giornataMatches.length === 1 && gIdx === giornate.length - 1) {
            roundName = 'Finale';
          } else if (giornataMatches.length === 2) {
            roundName = 'Semifinale';
          } else if (giornataMatches.length === 4) {
            roundName = 'Quarti';
          } else if (giornataMatches.length === 8) {
            roundName = 'Ottavi';
          } else {
            roundName = `Turno ${giornata}`;
          }
          
          // Crea chiave unica per evitare conflitti
          const key = `${round.name || 'bracket'}_${roundName}`;
          
          roundsMap[key] = {
            matches: giornataMatches,
            order: giornata,
            isHomeAway: round.is_home_away || false,
            displayName: roundName
          };
        });
      });
    } else if (matches && matches.length > 0) {
      // Per tornei nativi, raggruppa per campo round
      matches.forEach(match => {
        const roundName = match.round || 'main';
        if (!roundsMap[roundName]) {
          roundsMap[roundName] = {
            matches: [],
            order: getRoundOrder(roundName),
            isHomeAway: knockoutConfig?.format === 'home_away' && 
                        (knockoutConfig?.home_away_rounds || []).includes(roundName),
            displayName: getRoundDisplayName(roundName)
          };
        }
        roundsMap[roundName].matches.push(match);
      });
    }
    
    // Ordina i round per order (giornata)
    const sortedRounds = Object.entries(roundsMap)
      .sort((a, b) => (a[1].order || 0) - (b[1].order || 0))
      .filter(([name]) => !name.includes('third_place'));
    
    return sortedRounds;
  };
  
  const organizedRounds = organizeMatchesByRound();
  
  // Gestisci click su partita
  const handleMatchClick = (match, roundName) => {
    setSelectedMatch(match);
    setSelectedRound(roundName);
    if (onMatchClick) {
      onMatchClick(match);
    }
  };
  
  // Se non ci sono dati
  if (organizedRounds.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <div className="text-4xl mb-4">📊</div>
        <p>Nessun tabellone disponibile</p>
      </div>
    );
  }
  
  return (
    <div className="relative">
      {/* Scroll container */}
      <div 
        ref={containerRef}
        className="overflow-x-auto pb-4"
        style={{ scrollbarWidth: 'thin' }}
      >
        <div className="flex items-stretch gap-8 min-w-max p-4">
          {/* Round columns */}
          {organizedRounds.map(([roundName, roundData], idx) => (
            <React.Fragment key={roundName}>
              <RoundColumn
                roundName={roundName}
                displayName={roundData.displayName}
                matches={roundData.matches}
                isHomeAway={roundData.isHomeAway}
                onMatchClick={(match) => handleMatchClick(match, roundName)}
                animatingMatches={animatingMatches}
                bracketName={bracketName}
                isLastRound={idx === organizedRounds.length - 1}
              />
              
              {/* Connector line */}
              {idx < organizedRounds.length - 1 && (
                <div className="flex items-center">
                  <div className="w-8 h-0.5 bg-gray-300"></div>
                  <div className="text-gray-300">→</div>
                  <div className="w-8 h-0.5 bg-gray-300"></div>
                </div>
              )}
            </React.Fragment>
          ))}
          
          {/* Winner card */}
          {winner && (
            <>
              <div className="flex items-center">
                <div className="w-8 h-0.5 bg-yellow-400"></div>
                <div className="text-yellow-500">→</div>
                <div className="w-8 h-0.5 bg-yellow-400"></div>
              </div>
              <WinnerCard winner={winner} tournamentName={tournamentName || bracketName} />
            </>
          )}
        </div>
      </div>
      
      {/* Third place match */}
      {thirdPlaceMatch && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="text-sm font-bold text-gray-600 mb-3 flex items-center gap-2">
            🥉 Finale Terzo Posto
          </h4>
          <MatchCard
            match={thirdPlaceMatch}
            isHomeAway={false}
            onClick={(match) => handleMatchClick(match, 'third_place')}
          />
        </div>
      )}
      
      {/* Scroll hint for mobile */}
      <div className="absolute right-0 top-1/2 -translate-y-1/2 bg-gradient-to-l from-white via-white to-transparent w-16 h-full pointer-events-none md:hidden"></div>
      
      {/* Match detail popup */}
      {selectedMatch && (
        <MatchDetailPopup
          match={selectedMatch}
          isHomeAway={selectedMatch.is_home_away || 
            (knockoutConfig?.format === 'home_away' && 
             (knockoutConfig?.home_away_rounds || []).includes(selectedRound))}
          onClose={() => {
            setSelectedMatch(null);
            setSelectedRound(null);
          }}
          bracketName={bracketName}
          roundName={selectedRound}
        />
      )}
    </div>
  );
};

// ============================================
// MULTI-BRACKET TABS (per fasce)
// ============================================

export const MultiBracketTabs = ({
  brackets = {},
  knockoutConfig = {},
  tournamentName = '',
  onMatchClick,
  isImported = false
}) => {
  const bracketKeys = Object.keys(brackets);
  const [activeTab, setActiveTab] = useState(bracketKeys[0] || '');
  
  if (bracketKeys.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <div className="text-4xl mb-4">📊</div>
        <p>Nessun tabellone disponibile</p>
      </div>
    );
  }
  
  // Single bracket - no tabs
  if (bracketKeys.length === 1) {
    const bracket = brackets[bracketKeys[0]];
    return (
      <BracketTree
        matches={bracket.matches || []}
        knockoutConfig={knockoutConfig}
        winner={bracket.winner}
        finalist={bracket.finalist}
        tournamentName={tournamentName}
        bracketName={bracket.name || bracketKeys[0]}
        thirdPlaceMatch={bracket.third_place_match}
        onMatchClick={onMatchClick}
        isImported={isImported}
      />
    );
  }
  
  // Multiple brackets - show tabs
  const getTabColor = (key) => {
    const lower = key.toLowerCase();
    if (lower.includes('oro') || lower.includes('gold') || lower.includes('1')) 
      return 'bg-yellow-100 text-yellow-800 border-yellow-400';
    if (lower.includes('argento') || lower.includes('silver') || lower.includes('2')) 
      return 'bg-gray-200 text-gray-700 border-gray-400';
    if (lower.includes('bronzo') || lower.includes('bronze') || lower.includes('3')) 
      return 'bg-amber-100 text-amber-800 border-amber-500';
    return 'bg-blue-100 text-blue-800 border-blue-400';
  };
  
  return (
    <div>
      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 pb-4">
        {bracketKeys.map((key, idx) => {
          const bracket = brackets[key];
          const isActive = activeTab === key;
          const colorClass = getTabColor(key);
          
          return (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`px-4 py-2 rounded-lg font-medium transition-all border-2 ${
                isActive 
                  ? `${colorClass} shadow-md scale-105` 
                  : 'bg-gray-50 text-gray-600 border-transparent hover:bg-gray-100'
              }`}
              data-testid={`bracket-tab-${key}`}
            >
              {idx === 0 && '🥇 '}
              {idx === 1 && '🥈 '}
              {idx === 2 && '🥉 '}
              {idx > 2 && `${idx + 1}ª `}
              {bracket.name || key}
            </button>
          );
        })}
      </div>
      
      {/* Active bracket */}
      {activeTab && brackets[activeTab] && (
        <BracketTree
          matches={brackets[activeTab].matches || []}
          knockoutConfig={knockoutConfig}
          winner={brackets[activeTab].winner}
          finalist={brackets[activeTab].finalist}
          tournamentName={tournamentName}
          bracketName={brackets[activeTab].name || activeTab}
          thirdPlaceMatch={brackets[activeTab].third_place_match}
          onMatchClick={onMatchClick}
          isImported={isImported}
        />
      )}
    </div>
  );
};

// ============================================
// IMPORTED TOURNAMENT BRACKET
// ============================================

export const ImportedTournamentBracket = ({
  knockoutBracket = {},
  tournamentName = '',
  onMatchClick
}) => {
  const rounds = knockoutBracket.rounds || [];
  const [activeTab, setActiveTab] = useState(0);
  
  if (rounds.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <div className="text-4xl mb-4">📊</div>
        <p>Nessun tabellone disponibile</p>
      </div>
    );
  }
  
  // ============================================
  // CALCOLO PIAZZAMENTI FASCIA
  // ============================================
  
  const calculateFasciaPlacements = (round) => {
    if (!round || !round.matches || round.matches.length === 0) {
      return { placements: [], hasThirdPlace: false };
    }
    
    const matches = [...round.matches];
    const placements = [];
    
    // Raggruppa partite per giornata (round)
    const matchesByGiornata = {};
    matches.forEach(m => {
      const g = m.giornata || 0;
      if (!matchesByGiornata[g]) matchesByGiornata[g] = [];
      matchesByGiornata[g].push(m);
    });
    
    const giornate = Object.keys(matchesByGiornata).map(Number).sort((a, b) => a - b);
    const finalGiornata = giornate[giornate.length - 1];
    const semifinalGiornata = giornate.length > 1 ? giornate[giornate.length - 2] : null;
    
    // Helper per determinare vincitore di una partita
    const getMatchWinner = (match) => {
      if (!match || !match.giocata) return { winner: null, loser: null };
      
      const home = match.giocatore_casa;
      const away = match.giocatore_trasferta;
      const hg = match.gol_casa || 0;
      const ag = match.gol_trasferta || 0;
      const hr = match.rigori_casa;
      const ar = match.rigori_trasferta;
      
      if (hg > ag) return { winner: home, loser: away };
      if (ag > hg) return { winner: away, loser: home };
      if (hr !== null && ar !== null) {
        return ar > hr ? { winner: away, loser: home } : { winner: home, loser: away };
      }
      return { winner: null, loser: null };
    };
    
    // Trova la finale
    const finalMatches = matchesByGiornata[finalGiornata] || [];
    const finalMatch = finalMatches.find(m => 
      !m.is_third_place && matchesByGiornata[finalGiornata].length === 1
    ) || finalMatches[0];
    
    // Verifica se esiste finale 3° posto
    const thirdPlaceMatch = finalMatches.find(m => m.is_third_place) || 
                            (finalMatches.length > 1 ? finalMatches[1] : null);
    const hasThirdPlace = thirdPlaceMatch && thirdPlaceMatch.giocata;
    
    // 1° e 2° dalla finale
    if (finalMatch) {
      const { winner, loser } = getMatchWinner(finalMatch);
      if (winner) placements.push({ position: 1, name: winner, medal: '🥇', label: 'Vincitore' });
      if (loser) placements.push({ position: 2, name: loser, medal: '🥈', label: 'Finalista' });
    }
    
    // 3° e 4° posto
    if (hasThirdPlace) {
      // Con finale 3° posto - 3° e 4° separati
      const { winner: third, loser: fourth } = getMatchWinner(thirdPlaceMatch);
      if (third) placements.push({ position: 3, name: third, medal: '🥉', label: '3° posto' });
      if (fourth) placements.push({ position: 4, name: fourth, medal: '', label: '4° posto' });
    } else if (semifinalGiornata) {
      // Senza finale 3° posto - perdenti SF sono entrambi 3°-4°
      const semifinalMatches = matchesByGiornata[semifinalGiornata] || [];
      const sfLosers = [];
      
      // Ottieni i finalisti dalla finale per dedurre i perdenti SF
      const finalists = [];
      if (finalMatch) {
        finalists.push(finalMatch.giocatore_casa);
        finalists.push(finalMatch.giocatore_trasferta);
      }
      
      semifinalMatches.forEach(m => {
        const home = m.giocatore_casa;
        const away = m.giocatore_trasferta;
        const { loser } = getMatchWinner(m);
        
        if (loser) {
          // Perdente determinato - verifica che non sia in finale (safety check)
          if (!finalists.includes(loser)) {
            sfLosers.push(loser);
          }
        } else {
          // Non posso determinare il vincitore (es. pareggio senza rigori)
          // Deduco il perdente: è chi NON appare in finale
          if (finalists.length > 0) {
            if (!finalists.includes(home) && home) {
              sfLosers.push(home);
            }
            if (!finalists.includes(away) && away) {
              sfLosers.push(away);
            }
          }
        }
      });
      
      // Rimuovi duplicati
      const uniqueLosers = [...new Set(sfLosers)];
      
      if (uniqueLosers.length > 0) {
        placements.push({ 
          position: 3, 
          name: uniqueLosers.join(' / '), 
          medal: '🥉', 
          label: '3°-4° posto',
          isShared: true,
          players: uniqueLosers
        });
      }
    }
    
    // Quarti e oltre (se presenti)
    const quartiGiornata = giornate.length > 2 ? giornate[giornate.length - 3] : null;
    if (quartiGiornata) {
      const quartiMatches = matchesByGiornata[quartiGiornata] || [];
      const quartiLosers = [];
      quartiMatches.forEach(m => {
        const { loser } = getMatchWinner(m);
        if (loser) quartiLosers.push(loser);
      });
      
      if (quartiLosers.length > 0) {
        const startPos = hasThirdPlace ? 5 : 5;
        const endPos = startPos + quartiLosers.length - 1;
        placements.push({ 
          position: startPos, 
          name: quartiLosers.join(' / '), 
          medal: '', 
          label: `${startPos}°-${endPos}° posto`,
          isShared: true,
          players: quartiLosers
        });
      }
    }
    
    return { placements, hasThirdPlace };
  };
  
  // Helper function to determine winner of a bracket/round
  const determineBracketWinner = (round) => {
    if (!round || !round.matches || round.matches.length === 0) {
      return null;
    }
    
    // Find the final match (highest giornata or last match)
    const matches = [...round.matches];
    matches.sort((a, b) => (b.giornata || 0) - (a.giornata || 0));
    const finalMatch = matches[0];
    
    if (!finalMatch || !finalMatch.giocata) {
      return null;
    }
    
    const homeGoals = finalMatch.gol_casa || 0;
    const awayGoals = finalMatch.gol_trasferta || 0;
    const homePen = finalMatch.rigori_casa;
    const awayPen = finalMatch.rigori_trasferta;
    const home = finalMatch.giocatore_casa;
    const away = finalMatch.giocatore_trasferta;
    
    // Determine winner
    if (homeGoals > awayGoals) {
      return home;
    } else if (awayGoals > homeGoals) {
      return away;
    } else if (homePen !== null && awayPen !== null) {
      // Penalties - higher score wins
      return awayPen > homePen ? away : home;
    }
    
    return null;
  };
  
  // Check if we have multiple distinct brackets (fasce)
  const hasMultipleBrackets = rounds.some(r => r.is_main_bracket !== undefined) && 
                               rounds.length > 1 &&
                               rounds.every(r => r.matches?.length <= 4);
  
  if (hasMultipleBrackets) {
    const currentRound = rounds[activeTab];
    const { placements, hasThirdPlace } = calculateFasciaPlacements(currentRound);
    
    // Mostra come tab separati
    return (
      <div>
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 pb-4">
          {rounds.map((round, idx) => {
            const isMain = round.is_main_bracket;
            const isActive = activeTab === idx;
            
            return (
              <button
                key={idx}
                onClick={() => setActiveTab(idx)}
                className={`px-4 py-2 rounded-lg font-medium transition-all border-2 ${
                  isActive 
                    ? isMain 
                      ? 'bg-yellow-100 text-yellow-800 border-yellow-400 shadow-md' 
                      : 'bg-blue-100 text-blue-800 border-blue-400 shadow-md'
                    : 'bg-gray-50 text-gray-600 border-transparent hover:bg-gray-100'
                }`}
              >
                {isMain && '⭐ '}
                {round.name || `Fascia ${idx + 1}`}
              </button>
            );
          })}
        </div>
        
        {/* Active bracket - calculate winner from final match */}
        <BracketTree
          rounds={[currentRound]}
          winner={determineBracketWinner(currentRound)}
          tournamentName={tournamentName}
          bracketName={currentRound?.name}
          onMatchClick={onMatchClick}
          isImported={true}
        />
        
        {/* Riepilogo piazzamenti fascia */}
        {placements.length > 0 && (
          <div className="mt-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200">
            <h4 className="text-sm font-bold text-gray-600 mb-3 flex items-center gap-2">
              🏅 Piazzamenti {currentRound?.name || 'Fascia'}
              {!hasThirdPlace && (
                <span className="text-xs font-normal text-gray-400">(senza finale 3° posto)</span>
              )}
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {placements.map((p, idx) => (
                <div 
                  key={idx}
                  className={`p-3 rounded-lg text-center ${
                    p.position === 1 ? 'bg-yellow-100 border-2 border-yellow-400' :
                    p.position === 2 ? 'bg-gray-200 border-2 border-gray-400' :
                    p.position === 3 ? 'bg-amber-100 border-2 border-amber-400' :
                    'bg-white border border-gray-200'
                  }`}
                >
                  <div className="text-2xl mb-1">{p.medal || `${p.position}°`}</div>
                  <div className="font-bold text-gray-800 text-sm truncate" title={p.name}>
                    {p.isShared && p.players ? (
                      <div className="flex flex-col gap-0.5">
                        {p.players.map((player, i) => (
                          <span key={i} className="text-xs">{player}</span>
                        ))}
                      </div>
                    ) : p.name}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{p.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
  
  // Single bracket with multiple rounds (standard knockout tree)
  return (
    <BracketTree
      rounds={rounds}
      winner={knockoutBracket.winner}
      finalist={knockoutBracket.finalist}
      tournamentName={tournamentName}
      bracketName={knockoutBracket.main_bracket_name || ''}
      onMatchClick={onMatchClick}
      isImported={true}
    />
  );
};

export default BracketTree;
